// src/Pages/Admin/DetailsEstabilishmentUser/index.tsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, Switch } from "@mui/material";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {
  getEstabilishmentUserById,
  moderateEstabilishmentUser,
} from "../../../Services/Stripe";
import {
  IEstabilishmentUserDetail,
  IRestGetEstabilishmentUserDetail,
} from "../../../Services/Interfaces";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { api } from "../../../Services/Api";
import { planoPorVeiculacao } from "../../../Services/Plan";

const AuthImage: React.FC<{
  photoId: string | null | undefined;
  style?: React.CSSProperties;
  fallback: React.ReactNode;
}> = ({ photoId, style, fallback }) => {
  const [src, setSrc] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!photoId) return;
    let objectUrl: string;
    api
      .get(`/pt_BR/midway_photo/${photoId}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res: any) => {
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photoId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!src) return <>{fallback}</>;
  return <img src={src} alt="" style={style} />;
};

dayjs.locale("pt-br");

// ─── Icons ────────────────────────────────────────────────────────────────────

const ic = (p: React.ReactNode, size = 18) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {p}
  </svg>
);

const IconBack = () =>
  ic(
    <>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </>,
  );
const IconStore = () =>
  ic(
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>,
  );
const IconFile = () =>
  ic(
    <>
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <polyline points="13 2 13 9 20 9" />
    </>,
  );
const IconImage = () =>
  ic(
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </>,
  );
const IconDownload = () =>
  ic(
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </>,
    16,
  );
const IconUser = () =>
  ic(
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>,
    40,
  );
const IconPin = () =>
  ic(
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>,
    14,
  );

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null) => (d ? dayjs(d).format("DD/MM/YYYY") : "—");
const fmtDateTime = (d: string | null) =>
  d ? dayjs(d).format("DD/MM/YYYY [às] HH:mm") : "—";

const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> =
  {
    active: { label: "Ativo", color: "#1a7a4a", bg: "#e6f7ef" },
    trialing: { label: "Período de Teste", color: "#0066cc", bg: "#e5f0ff" },
    past_due: { label: "Pagamento Pendente", color: "#b45309", bg: "#fff7e6" },
    incomplete: { label: "Incompleto", color: "#b45309", bg: "#fff7e6" },
    incomplete_expired: { label: "Expirado", color: "#888", bg: "#f0f0f0" },
    canceled: { label: "Cancelado", color: "#b91c1c", bg: "#fef2f2" },
    unpaid: { label: "Não Pago", color: "#b91c1c", bg: "#fef2f2" },
  };
const getStatusCfg = (s: string) =>
  STATUS_CFG[s] ?? { label: s, color: "#888", bg: "#f0f0f0" };

const isImage = (mime: string | null) =>
  !!mime &&
  (mime.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "webp"].some((e) => mime.includes(e)));

// ─── Component ────────────────────────────────────────────────────────────────

export const DetailsEstabilishmentUser = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<IEstabilishmentUserDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Toggle dialog
  const [toggleOpen, setToggleOpen] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  // ── Edit dialog ──────────────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [editInsta, setEditInsta] = useState("");
  const [editSite, setEditSite] = useState("");
  // address
  const [editCep, setEditCep] = useState("");
  const [editStreet, setEditStreet] = useState("");
  const [editNumber, setEditNumber] = useState("");
  const [editCompl, setEditCompl] = useState("");
  const [editDistr, setEditDistr] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editUf, setEditUf] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  // password
  const [editPwd, setEditPwd] = useState("");
  const [editPwdConf, setEditPwdConf] = useState("");

  const openEdit = () => {
    if (!user) return;
    const addr = user.profile?.address;
    setEditEmail(user.email ?? "");
    setEditInsta(user.profile?.instagram ?? "");
    setEditSite(user.profile?.site ?? "");
    setEditCep(addr?.postalCode ?? "");
    setEditStreet(addr?.street ?? "");
    setEditNumber(addr?.number ?? "");
    setEditCompl(addr?.complement ?? "");
    setEditDistr(addr?.district ?? "");
    setEditCity(addr?.city?.name ?? "");
    setEditUf(addr?.state?.shortName ?? addr?.state?.name ?? "");
    setEditPwd("");
    setEditPwdConf("");
    setEditOpen(true);
  };

  const handleCepBlur = async () => {
    const cep = editCep.replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await res.json();
      if (!d.erro) {
        setEditStreet(d.logradouro ?? "");
        setEditDistr(d.bairro ?? "");
        setEditCity(d.localidade ?? "");
        setEditUf(d.uf ?? "");
      }
    } catch (_) {
      /* silencioso */
    } finally {
      setCepLoading(false);
    }
  };

  const maskCep = (v: string) => {
    const d = v.replace(/\D/g, "").slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
  };

  const handleEditSave = async () => {
    if (editPwd && editPwd !== editPwdConf) {
      toast.warning("As novas senhas não coincidem.");
      return;
    }
    setEditSaving(true);
    try {
      await api.put(
        `/admin/user_estabilishment/${userId}`,
        {
          email: editEmail.trim() || undefined,
          profile: {
            instagram: editInsta.trim() || null,
            site: editSite.trim() || null,
            address: {
              postalCode: editCep.replace(/\D/g, ""),
              street: editStreet.trim(),
              number: editNumber.trim(),
              complement: editCompl.trim(),
              district: editDistr.trim(),
              city: editCity.trim(),
              state: editUf.trim(),
              stateShort: editUf.trim(),
              country: "Brasil",
            },
          },
          ...(editPwd ? { newPassword: editPwd } : {}),
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      toast.success("Usuário atualizado com sucesso.");
      setEditOpen(false);
      loadUser();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Erro ao salvar.");
    } finally {
      setEditSaving(false);
    }
  };

  // ── Load ─────────────────────────────────────────────────────────────────────

  const loadUser = () => {
    if (!userId) return;
    setLoading(true);
    getEstabilishmentUserById(userId)
      .then((res) => {
        const { data } = res as IRestGetEstabilishmentUserDetail;
        setUser(data.data);
      })
      .catch(() => toast.error("Erro ao carregar usuário."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUser();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleToggleConfirm = () => {
    if (!user) return;
    setToggleLoading(true);
    moderateEstabilishmentUser(user.id, !user.isModered)
      .then(() => {
        toast.success(`Usuário ${!user.isModered ? "aprovado" : "reprovado"}!`);
        setToggleOpen(false);
        loadUser();
      })
      .catch(() => toast.error("Erro ao moderar usuário."))
      .finally(() => setToggleLoading(false));
  };

  // ── Styles ────────────────────────────────────────────────────────────────────

  const card: React.CSSProperties = {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #f0f0f0",
    padding: "20px 24px",
    marginBottom: 16,
  };
  const sTitle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: "#999",
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 14,
  };
  const row: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  };
  const label: React.CSSProperties = {
    fontSize: 13,
    color: "#aaa",
    minWidth: 120,
  };
  const value: React.CSSProperties = {
    fontSize: 14,
    color: "#222",
    fontWeight: 500,
  };
  const btnTeal: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 18px",
    background: "#00b6bc",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
  };
  const btnOutline: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 18px",
    background: "#fff",
    color: "#444",
    border: "1.5px solid #ddd",
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
  };
  const dBtn: React.CSSProperties = {
    padding: "10px 24px",
    borderRadius: 10,
    border: "1.5px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    color: "#555",
  };
  const dBtnP: React.CSSProperties = {
    padding: "10px 24px",
    borderRadius: 10,
    border: "none",
    background: "#00b6bc",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    color: "#fff",
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading)
    return (
      <div
        style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}
      >
        <CircularProgress style={{ color: "#00b6bc" }} />
      </div>
    );

  if (!user)
    return (
      <div style={{ padding: 32, color: "#aaa" }}>Usuário não encontrado.</div>
    );

  const sub = user.subscription;
  const subCfg = sub ? getStatusCfg(sub.status) : null;

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Back + title */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{ ...btnOutline, padding: "8px 14px" }}
        >
          <IconBack />
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111", margin: 0 }}>
          {user.profile?.fullName ?? user.email}
        </h1>
        <span
          style={{
            padding: "3px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            background: user.isModered ? "#e6f7ef" : "#fff7e6",
            color: user.isModered ? "#1a7a4a" : "#b45309",
          }}
        >
          {user.isModered ? "Aprovado" : "Aguardando aprovação"}
        </span>
      </div>

      {/* ── User info card ─────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {/* Avatar */}
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ccc",
              flexShrink: 0,
            }}
          >
            <IconUser />
          </div>

          {/* Info */}
          <div style={{ flex: 1 }}>
            <div style={row}>
              <span style={label}>Nome</span>
              <span style={value}>{user.profile?.fullName ?? "—"}</span>
            </div>
            <div style={row}>
              <span style={label}>E-mail</span>
              <span style={value}>{user.email}</span>
            </div>
            <div style={row}>
              <span style={label}>Instagram</span>
              <span style={value}>
                {user.profile?.instagram ? `@${user.profile.instagram}` : "—"}
              </span>
            </div>
            <div style={row}>
              <span style={label}>Site</span>
              <span style={value}>{user.profile?.site ?? "—"}</span>
            </div>
            <div style={row}>
              <span style={label}>Cadastrado em</span>
              <span style={value}>{fmtDateTime(user.createdAt)}</span>
            </div>
            <div style={row}>
              <span style={label}>Status conta</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Switch
                  size="small"
                  checked={!!user.isModered}
                  onChange={() => setToggleOpen(true)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#00b6bc" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#00b6bc",
                    },
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: user.isModered ? "#00b6bc" : "#999",
                  }}
                >
                  {user.isModered
                    ? "Inativar estabelecimento"
                    : "Ativar estabelecimento"}
                </span>
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              <button onClick={openEdit} style={btnTeal}>
                Editar usuário
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Subscription card ──────────────────────────────────────────────── */}
      <div style={card}>
        <p style={sTitle}>Assinatura</p>
        {sub ? (
          <div>
            <div style={row}>
              <span style={label}>Plano</span>
              <span style={value}>{sub.plan?.name ?? "—"}</span>
              {sub.plan && (
                <span
                  style={{ fontSize: 13, color: "#00b6bc", fontWeight: 600 }}
                >
                  {`R$ ${parseFloat(sub.plan.price).toFixed(2)}${
                    planoPorVeiculacao(sub.plan.name) ? "" : "/mês"
                  }`}
                </span>
              )}
            </div>
            <div style={row}>
              <span style={label}>Status</span>
              <span
                style={{
                  padding: "3px 12px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  background: subCfg!.bg,
                  color: subCfg!.color,
                }}
              >
                {subCfg!.label}
              </span>
            </div>
            <div style={row}>
              <span style={label}>Período</span>
              <span style={value}>
                {fmtDate(sub.currentPeriodStart)} →{" "}
                {fmtDate(sub.currentPeriodEnd)}
              </span>
            </div>
          </div>
        ) : (
          <p style={{ color: "#bbb", fontSize: 14, margin: 0 }}>
            Sem assinatura ativa.
          </p>
        )}
      </div>

      {/* ── Establishments + attachments ───────────────────────────────────── */}
      <div style={card}>
        <p style={sTitle}>Estabelecimentos</p>

        {user.userEstabilishments.length === 0 ? (
          <p style={{ color: "#bbb", fontSize: 14, margin: 0 }}>
            Nenhum estabelecimento vinculado.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {user.userEstabilishments.map((ue) => (
              <div
                key={ue.id}
                style={{
                  border: "1.5px solid #f0f0f0",
                  borderRadius: 12,
                  padding: "16px 18px",
                }}
              >
                {/* Establishment header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 10,
                      background: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #efefef",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    <AuthImage
                      photoId={ue.estabilishment?.image}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      fallback={
                        <span style={{ color: "#ccc" }}>
                          <IconStore />
                        </span>
                      }
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#111",
                        marginBottom: 4,
                      }}
                    >
                      {ue.estabilishment?.name ?? "—"}
                    </div>
                    {ue.estabilishment?.addressInGoogleFormat && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 12,
                          color: "#888",
                        }}
                      >
                        <IconPin />
                        {ue.estabilishment.addressInGoogleFormat}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#bbb", marginTop: 4 }}>
                      Vinculado em {fmtDate(ue.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Attachments */}
                <div>
                  <p
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#aaa",
                      margin: "0 0 8px",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Documentos enviados ({ue.attachments.length})
                  </p>

                  {ue.attachments.length === 0 ? (
                    <p style={{ fontSize: 13, color: "#bbb", margin: 0 }}>
                      Nenhum documento.
                    </p>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {ue.attachments.map((att) => (
                        <div
                          key={att.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "8px 12px",
                            background: "#fafafa",
                            borderRadius: 8,
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <span style={{ color: "#00b6bc", flexShrink: 0 }}>
                            {isImage(att.mimeType) ? (
                              <IconImage />
                            ) : (
                              <IconFile />
                            )}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#333",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {att.originalName ?? att.filename ?? "Documento"}
                            </div>
                            <div style={{ fontSize: 11, color: "#aaa" }}>
                              {att.mimeType ?? "—"} · {fmtDate(att.createdAt)}
                            </div>
                          </div>
                          {att.filename && (
                            <a
                              href={`${process.env.REACT_APP_DIGITAL_ASSETS_URL ?? ""}/upload_user_estabilishment_attachment/${att.filename}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                ...btnTeal,
                                padding: "6px 12px",
                                textDecoration: "none",
                              }}
                              title="Visualizar documento"
                            >
                              <IconDownload /> Ver
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Dialog: Edit user ────────────────────────────────────────────── */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ fontWeight: 700, fontSize: 18 }}>
          Editar usuário
        </DialogTitle>
        <DialogContent style={{ paddingTop: 4 }}>
          {(() => {
            const inp: React.CSSProperties = {
              width: "100%",
              padding: "9px 12px",
              border: "1.5px solid #e8e8e8",
              borderRadius: 8,
              fontSize: 13,
              color: "#333",
              outline: "none",
              background: "#fff",
              boxSizing: "border-box",
            };
            const lbl: React.CSSProperties = {
              fontSize: 12,
              fontWeight: 600,
              color: "#666",
              display: "block",
              marginBottom: 4,
              marginTop: 14,
            };
            return (
              <>
                {/* Email */}
                <label style={lbl}>E-mail</label>
                <input
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  type="email"
                  style={inp}
                />

                {/* Instagram + Site */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                  }}
                >
                  <div>
                    <label style={lbl}>Instagram</label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1.5px solid #e8e8e8",
                        borderRadius: 8,
                        background: "#fff",
                      }}
                    >
                      <span
                        style={{
                          padding: "9px 0 9px 10px",
                          color: "#aaa",
                          fontSize: 13,
                        }}
                      >
                        @
                      </span>
                      <input
                        value={editInsta}
                        onChange={(e) =>
                          setEditInsta(e.target.value.replace(/^@/, ""))
                        }
                        style={{ ...inp, border: "none", paddingLeft: 2 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Site</label>
                    <input
                      value={editSite}
                      onChange={(e) => setEditSite(e.target.value)}
                      type="url"
                      style={inp}
                      placeholder="www.exemplo.com.br"
                    />
                  </div>
                </div>

                {/* Address */}
                <p
                  style={{
                    ...lbl,
                    marginTop: 20,
                    fontSize: 13,
                    color: "#333",
                    fontWeight: 700,
                  }}
                >
                  Endereço
                </p>
                <label style={lbl}>CEP</label>
                <div style={{ position: "relative" }}>
                  <input
                    value={editCep}
                    onChange={(e) => setEditCep(maskCep(e.target.value))}
                    onBlur={handleCepBlur}
                    placeholder="00000-000"
                    maxLength={9}
                    style={inp}
                    inputMode="numeric"
                  />
                  {cepLoading && (
                    <CircularProgress
                      size={12}
                      style={{
                        color: "#00b6bc",
                        position: "absolute",
                        right: 10,
                        top: 11,
                      }}
                    />
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 80px",
                    gap: 10,
                  }}
                >
                  <div>
                    <label style={lbl}>Endereço</label>
                    <input
                      value={editStreet}
                      onChange={(e) => setEditStreet(e.target.value)}
                      style={inp}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Número</label>
                    <input
                      value={editNumber}
                      onChange={(e) => setEditNumber(e.target.value)}
                      style={inp}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                  }}
                >
                  <div>
                    <label style={lbl}>Complemento</label>
                    <input
                      value={editCompl}
                      onChange={(e) => setEditCompl(e.target.value)}
                      style={inp}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Bairro</label>
                    <input
                      value={editDistr}
                      onChange={(e) => setEditDistr(e.target.value)}
                      style={inp}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 80px",
                    gap: 10,
                  }}
                >
                  <div>
                    <label style={lbl}>Cidade</label>
                    <input
                      value={editCity}
                      onChange={(e) => setEditCity(e.target.value)}
                      style={inp}
                    />
                  </div>
                  <div>
                    <label style={lbl}>UF</label>
                    <input
                      value={editUf}
                      onChange={(e) => setEditUf(e.target.value.toUpperCase())}
                      maxLength={2}
                      style={inp}
                    />
                  </div>
                </div>

                {/* Password */}
                <p
                  style={{
                    ...lbl,
                    marginTop: 20,
                    fontSize: 13,
                    color: "#333",
                    fontWeight: 700,
                  }}
                >
                  Nova senha{" "}
                  <span style={{ fontWeight: 400, color: "#aaa" }}>
                    (deixe em branco para não alterar)
                  </span>
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                  }}
                >
                  <div>
                    <label style={lbl}>Nova senha</label>
                    <input
                      value={editPwd}
                      onChange={(e) => setEditPwd(e.target.value)}
                      type="password"
                      style={inp}
                    />
                  </div>
                  <div>
                    <label style={lbl}>Confirmar senha</label>
                    <input
                      value={editPwdConf}
                      onChange={(e) => setEditPwdConf(e.target.value)}
                      type="password"
                      style={inp}
                    />
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px", gap: 8 }}>
          <button style={dBtn} onClick={() => setEditOpen(false)}>
            Cancelar
          </button>
          <button
            style={{ ...dBtnP, opacity: editSaving ? 0.7 : 1 }}
            disabled={editSaving}
            onClick={handleEditSave}
          >
            {editSaving ? "Salvando..." : "Salvar"}
          </button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Toggle status ─────────────────────────────────────────── */}
      <Dialog
        open={toggleOpen}
        onClose={() => setToggleOpen(false)}
        PaperProps={{ style: { borderRadius: 16, padding: 8, minWidth: 340 } }}
      >
        <DialogTitle
          style={{ fontWeight: 700, fontSize: 18, paddingBottom: 8 }}
        >
          {user.isModered ? "Inativar usuário" : "Ativar usuário"}
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0 }}>
          <p style={{ fontSize: 14, color: "#555", margin: 0 }}>
            {user.isModered
              ? "Tem certeza que deseja inativar este usuário?"
              : "Tem certeza que deseja ativar este usuário?"}
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px", gap: 8 }}>
          <button style={dBtn} onClick={() => setToggleOpen(false)}>
            Cancelar
          </button>
          <button
            style={{ ...dBtnP, opacity: toggleLoading ? 0.7 : 1 }}
            disabled={toggleLoading}
            onClick={handleToggleConfirm}
          >
            {toggleLoading ? "Aguarde..." : "Confirmar"}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};